import styled from 'styled-components';
import { machineTableDataStructure } from '../../../constants/machineTable/machineTableDataStructure.constant';
import {
  IDictionaryUnitMap,
  IDictionaryWordMap,
  TypeMachineProperties,
} from '../../../interfaces/Dictionary.interfaces';
import { IMachine } from '../../../interfaces/Machine.interfaces';

interface MachineSpecTableProps {
  machineData: IMachine;
  unitMap: IDictionaryUnitMap;
  wordMap: IDictionaryWordMap;
  type: 'long' | 'short';
  onProductDetail?: boolean;
}

export const MachineSpecTable = ({ machineData, unitMap, wordMap, type, onProductDetail }: MachineSpecTableProps) => {
  if (type === 'long') {
    return (
      <InfoSectionContainer>
        {machineTableDataStructure.long.map((infoSectionData) =>
          // required 한 섹션인 경우 (value가 비어 있다면 '-' 로 표시)
          infoSectionData.isRequiredSection ? (
            <InfoDetailSection onProductDetail={onProductDetail}>
              <InfoSectionTitle>{infoSectionData.sectionTitle}</InfoSectionTitle>

              <InfoItemList>
                {infoSectionData.sectionData.map((dataKey: TypeMachineProperties) => (
                  <InfoItemWrap>
                    <InfoItemKey>{wordMap[dataKey]}</InfoItemKey>

                    <InfoItemValueConatiner>
                      <InfoItemValue>
                        {/* 값이 비어있는 경우 - 로 표시 */}
                        {!machineData[dataKey] && '-'}

                        {/* 값이 있는 경우 값 + 단위 표시 */}
                        {machineData[dataKey] && typeof machineData[dataKey] === 'number'
                          ? machineData[dataKey].toLocaleString()
                          : machineData[dataKey]}
                        {machineData[dataKey] && unitMap[dataKey as keyof typeof unitMap]}
                      </InfoItemValue>
                    </InfoItemValueConatiner>
                  </InfoItemWrap>
                ))}
              </InfoItemList>
            </InfoDetailSection>
          ) : (
            // required 하지 않은 섹션인 경우 (value가 비어 있다면 item 렌더링 X)
            <InfoDetailSection onProductDetail={onProductDetail}>
              <InfoSectionTitle>{infoSectionData.sectionTitle}</InfoSectionTitle>

              <InfoItemList>
                {infoSectionData.sectionData.map((dataKey: TypeMachineProperties) =>
                  // 값이 있는 경우 item 렌더링
                  machineData[dataKey] ? (
                    <InfoItemWrap>
                      <InfoItemKey>{wordMap[dataKey]}</InfoItemKey>

                      <InfoItemValueConatiner>
                        <InfoItemValue>
                          {machineData[dataKey] && typeof machineData[dataKey] === 'number'
                            ? machineData[dataKey].toLocaleString()
                            : machineData[dataKey]}
                          {machineData[dataKey] && unitMap[dataKey as keyof typeof unitMap]}
                        </InfoItemValue>
                      </InfoItemValueConatiner>

                      <HR />
                    </InfoItemWrap>
                  ) : (
                    // 값이 없는 경우 item 렌더링 X
                    <></>
                  ),
                )}
              </InfoItemList>
            </InfoDetailSection>
          ),
        )}
      </InfoSectionContainer>
    );
  }

  if (type === 'short') {
    return (
      <InfoSectionContainer>
        <InfoDetailSection onProductDetail={onProductDetail}>
          <InfoSectionTitle>{machineTableDataStructure.short.sectionTitle}</InfoSectionTitle>

          <InfoItemList>
            {machineTableDataStructure.short.sectionData.map((dataKey: TypeMachineProperties) => {
              if (machineData[dataKey] && wordMap[dataKey]) {
                return (
                  <InfoItemWrap>
                    <InfoItemKey>{wordMap[dataKey]}</InfoItemKey>

                    <InfoItemValueConatiner>
                      <InfoItemValue>
                        {typeof machineData[dataKey] === 'number'
                          ? machineData[dataKey].toLocaleString()
                          : machineData[dataKey]}
                        {unitMap[dataKey as keyof typeof unitMap]}
                      </InfoItemValue>
                    </InfoItemValueConatiner>

                    <HR />
                  </InfoItemWrap>
                );
              }
              return <></>;
            })}
          </InfoItemList>
        </InfoDetailSection>
      </InfoSectionContainer>
    );
  }

  return <></>;
};

const InfoSectionContainer = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const InfoDetailSection = styled.div<{ onProductDetail?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const InfoSectionTitle = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const InfoItemList = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const InfoItemWrap = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;

  padding: 8px 0px;

  gap: 10px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey30, #d5d5e3);
`;

const InfoText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const InfoItemKey = styled(InfoText)`
  min-width: 150px;
`;

const InfoItemValueConatiner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  flex-grow: 1;
`;

const InfoItemValue = styled(InfoText)`
  color: var(--Light-Scheme-Monochrome-black, #232429);
`;

const HR = styled.hr`
  position: absolute;
  bottom: 0px;
  left: 0px;

  width: 100%;
`;
